import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";

import "leaflet/dist/leaflet.css";
import "./MapaEntregaApp.css";

// Mapa do "Acompanhar entrega" dos apps de entrega (PO1-3756, sprint 636).
//
// Diferente do MapaRotaEstimada (rota ilustrativa entre centróides), aqui a
// posição do entregador é REAL: vem do app (Lalamove, Uber Direct) pelo
// backend_hub -> hub-backend, com cache de 20 s no servidor. Três pontos:
// coleta, entrega e o entregador, que é movido com setLatLng a cada leitura.
//
// Polling de 30 s só com o modal aberto (`ativo`) e a aba do navegador visível
// (padrão do BidDetalhe); para quando o app diz que a entrega terminou
// (entregue, cancelada). Mesmo Leaflet puro do MapaRotaEstimada: import
// dinâmico, tiles OSM, circleMarker com as cores do tema, fitBounds,
// ResizeObserver e mapa.remove() na desmontagem.

export const POLL_MS = 30000;
const ZOOM_MAXIMO = 15;
const CAMADA_OSM = {
  url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: "&copy; OpenStreetMap",
};
const COR_COLETA_FALLBACK = "#8f9bba"; // theme-ok: fallback do Leaflet, cor real vem do token
const COR_ENTREGA_FALLBACK = "#1d8cf8"; // theme-ok: fallback do Leaflet, cor real vem do token
const COR_ENTREGADOR_FALLBACK = "#00f2c3"; // theme-ok: fallback do Leaflet, cor real vem do token

function corDoTema(token, fallback) {
  if (typeof window === "undefined" || !window.getComputedStyle) return fallback;
  const valor = window.getComputedStyle(document.documentElement).getPropertyValue(token);
  return (valor && valor.trim()) || fallback;
}

const ponto = (p) =>
  p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)) && p.lat !== null && p.lng !== null
    ? [Number(p.lat), Number(p.lng)]
    : null;

// Pontos desenháveis da resposta da API (cada um [lat, lng] ou null).
export function pontosDoMapa(posicao) {
  const p = posicao || {};
  return {
    coleta: ponto(p.coleta),
    entrega: ponto(p.entrega),
    entregador: ponto(p.entregador),
  };
}

// Continua consultando? Só enquanto a entrega não terminou e a leitura não
// voltou "sem dados" (envio em modo teste, encerrado).
export function deveContinuar(posicao) {
  if (!posicao) return true;
  if (posicao.final) return false;
  return posicao.status !== "sem-dados";
}

const MapaEntregaApp = ({ notaFiscalId, idEnvio, params, ativo = true }) => {
  const containerRef = useRef(null);
  const mapaRef = useRef(null);
  const leafletRef = useRef(null);
  const marcadoresRef = useRef({});
  const enquadradoRef = useRef(false);
  const observadorRef = useRef(null);
  const [posicao, setPosicao] = useState(null);
  const [erro, setErro] = useState("");

  const url = `/notas-fiscais/consulta/${notaFiscalId}/etiqueta/${idEnvio}/posicao`;

  const consultar = useCallback(async () => {
    try {
      const { data } = await api.get(url, { params });
      setPosicao(data || null);
      setErro(data && data.status === "falha" ? "O app não respondeu agora; tentamos de novo em instantes." : "");
    } catch (e) {
      setErro((e.response && e.response.data && e.response.data.error) || "Não foi possível consultar a posição do entregador.");
    }
  }, [url, params]);

  useEffect(() => {
    if (!ativo) return;
    consultar();
  }, [ativo, consultar]);

  const continuar = deveContinuar(posicao);
  useEffect(() => {
    if (!ativo || !continuar) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      consultar();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [ativo, continuar, consultar]);

  // Desenha (1ª vez) ou atualiza os marcadores a cada leitura.
  const pontos = pontosDoMapa(posicao);
  const temPonto = !!(pontos.coleta || pontos.entrega || pontos.entregador);
  useEffect(() => {
    if (!temPonto || !containerRef.current) return undefined;
    let cancelado = false;

    async function desenhar() {
      if (!leafletRef.current) {
        const modulo = await import("leaflet");
        leafletRef.current = modulo.default || modulo;
      }
      if (cancelado || !containerRef.current) return;
      const L = leafletRef.current;
      if (!mapaRef.current) {
        const mapa = L.map(containerRef.current, { scrollWheelZoom: false, zoomControl: true, attributionControl: true });
        mapaRef.current = mapa;
        L.tileLayer(CAMADA_OSM.url, { maxZoom: 18, attribution: CAMADA_OSM.attribution }).addTo(mapa);
        if (typeof ResizeObserver !== "undefined") {
          observadorRef.current = new ResizeObserver(() => {
            if (mapaRef.current) mapaRef.current.invalidateSize();
          });
          observadorRef.current.observe(containerRef.current);
        }
      }
      const mapa = mapaRef.current;
      const cores = {
        coleta: corDoTema("--everlog-card-muted", COR_COLETA_FALLBACK),
        entrega: corDoTema("--theme-accent", COR_ENTREGA_FALLBACK),
        entregador: corDoTema("--everlog-status-success", COR_ENTREGADOR_FALLBACK),
      };
      const rotulos = { coleta: "Coleta", entrega: "Entrega", entregador: "Entregador" };
      Object.keys(rotulos).forEach((k) => {
        const p = pontos[k];
        const atual = marcadoresRef.current[k];
        if (!p) {
          if (atual) {
            atual.remove();
            delete marcadoresRef.current[k];
          }
          return;
        }
        if (atual) {
          atual.setLatLng(p);
          return;
        }
        marcadoresRef.current[k] = L.circleMarker(p, {
          radius: k === "entregador" ? 9 : 7,
          color: cores[k],
          fillColor: cores[k],
          fillOpacity: 0.9,
          weight: 2,
        })
          .addTo(mapa)
          .bindTooltip(rotulos[k], { direction: "top", offset: [0, -8] });
      });
      // Enquadra uma vez; depois o usuário controla o zoom.
      if (!enquadradoRef.current) {
        const todos = [pontos.coleta, pontos.entrega, pontos.entregador].filter(Boolean);
        if (todos.length) {
          mapa.fitBounds(L.latLngBounds(todos), { maxZoom: ZOOM_MAXIMO, padding: [24, 24] });
          enquadradoRef.current = true;
        }
      }
      mapa.invalidateSize();
    }

    desenhar();
    return () => {
      cancelado = true;
    };
    // pontos é derivado de posicao: redesenha a cada leitura nova
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posicao, temPonto]);

  // Desmontagem: solta o mapa e o observador.
  useEffect(
    () => () => {
      if (observadorRef.current) {
        observadorRef.current.disconnect();
        observadorRef.current = null;
      }
      if (mapaRef.current) {
        mapaRef.current.remove();
        mapaRef.current = null;
      }
      marcadoresRef.current = {};
    },
    []
  );

  if (posicao && posicao.status === "sem-dados") {
    return (
      <div className="mapa-entrega-app mapa-entrega-app--empty" data-testid="mapa-entrega-vazio">
        <i className="tim-icons icon-square-pin" aria-hidden="true" />
        <span>{posicao.motivo || "Ainda não há posição do entregador para esta entrega."}</span>
      </div>
    );
  }

  const ent = posicao && posicao.entregador;
  const status = posicao ? posicao.descricao || posicao.statusApp || "-" : "Consultando o app...";
  // Nota abaixo do mapa: frases separadas por ponto ("Carlos · Moto · ABC1D23. Atualiza a cada 30 s.").
  const nota = [
    ent
      ? [ent.nome, ent.veiculo, ent.placa].filter(Boolean).join(" · ") || "Entregador a caminho"
      : posicao && !posicao.final
        ? "Procurando entregador"
        : "",
    posicao && continuar ? "Atualiza a cada 30 s" : "",
  ].filter(Boolean);
  return (
    <div className="mapa-entrega-app" data-testid="mapa-entrega-app">
      <div className="mapa-entrega-app__header">
        <strong data-testid="mapa-entrega-status">{status.charAt(0).toUpperCase() + status.slice(1)}</strong>
        {posicao && posicao.etaMinutos != null && <span>Previsão: {posicao.etaMinutos} min</span>}
      </div>
      {temPonto ? (
        <div ref={containerRef} className="mapa-entrega-app__canvas" />
      ) : (
        posicao && (
          <div className="mapa-entrega-app--empty">
            <span>O app ainda não informou a localização da coleta e da entrega.</span>
          </div>
        )
      )}
      <p className="mapa-entrega-app__note" data-testid="mapa-entrega-entregador">
        {nota.length ? `${nota.join(". ")}.` : ""}
      </p>
      {erro && <p className="mapa-entrega-app__note mapa-entrega-app__note--erro">{erro}</p>}
    </div>
  );
};

export default MapaEntregaApp;
